import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { Clock, Target, Scale, X, ChevronDown, ChevronUp } from "lucide-react";

/* Courtroom Mode — λιτό, high-contrast view για tablet/κινητό στο ακροατήριο.
   Μόνο τα κρίσιμα: χρονολόγιο, ερωτήσεις αντεξέτασης, αποδεικτικά. */

const TABS = [
  { key: "timeline", label: "Χρονολόγιο", icon: Clock },
  { key: "cross", label: "Αντεξέταση", icon: Target },
  { key: "evidence", label: "Αποδεικτικά", icon: Scale },
];

const IMP_COLOR = {
  critical: "border-red-500 text-red-300",
  high: "border-amber-500 text-amber-300",
  medium: "border-blue-500 text-blue-300",
  low: "border-slate-600 text-slate-400",
};

function mdToPlainBlocks(md) {
  // Απλή ανάγνωση markdown: επικεφαλίδες + bullets ως μεγάλες γραμμές
  return (md || "").split("\n").map(l => l.trim()).filter(Boolean);
}

export default function Courtroom() {
  const { currentCase } = useApp();
  const [tab, setTab] = useState("timeline");
  const [timeline, setTimeline] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [crossOutputs, setCrossOutputs] = useState([]);
  const [openCross, setOpenCross] = useState(null);

  useEffect(() => {
    if (!currentCase) return;
    api.get(`/cases/${currentCase.id}/timeline`).then(r => setTimeline(r.data)).catch(() => {});
    api.get(`/cases/${currentCase.id}/evidence`).then(r => setEvidence(r.data)).catch(() => {});
    api.get(`/cases/${currentCase.id}/ai-outputs`).then(r =>
      setCrossOutputs((r.data || []).filter(o =>
        ["cross_examination", "witness_contradictions", "devils_advocate"].includes(o.prompt_key)))
    ).catch(() => {});
  }, [currentCase]);

  const sortedTimeline = useMemo(() =>
    [...timeline].sort((a, b) =>
      `${a.date || ""}${a.time || ""}`.localeCompare(`${b.date || ""}${b.time || ""}`)),
    [timeline]);

  if (!currentCase) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl mb-4">Δεν έχει επιλεγεί υπόθεση</div>
          <Link to="/" className="text-amber-400 underline text-lg">Επιστροφή</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="courtroom-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/95 border-b border-amber-500/40 px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-400 font-mono">Courtroom Mode</div>
          <div className="text-lg font-semibold truncate">{currentCase.title}</div>
        </div>
        <Link to="/" data-testid="courtroom-exit"
          className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-amber-500/50 text-amber-300 text-base">
          <X className="size-5"/>Έξοδος
        </Link>
      </div>

      {/* Tabs — μεγάλα touch targets */}
      <div className="grid grid-cols-3 border-b border-white/15">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`courtroom-tab-${key}`}
            className={`flex items-center justify-center gap-2 py-4 text-lg font-semibold transition-colors
              ${tab === key ? "bg-amber-500/15 text-amber-300 border-b-4 border-amber-400" : "text-slate-300"}`}>
            <Icon className="size-5"/>{label}
          </button>
        ))}
      </div>

      <div className="p-4 pb-24 max-w-3xl mx-auto">
        {tab === "timeline" && (
          <div className="space-y-3">
            {sortedTimeline.length === 0 && <div className="text-slate-400 text-lg py-8 text-center">Κενό χρονολόγιο</div>}
            {sortedTimeline.map(ev => (
              <div key={ev.id}
                className={`rounded-xl border-l-8 bg-white/5 p-4 ${IMP_COLOR[ev.importance] || IMP_COLOR.low}`}>
                <div className="font-mono text-base text-amber-300">
                  {(ev.date || "").slice(0, 10)}{ev.time ? ` · ${ev.time}` : ""}
                </div>
                <div className="text-xl font-semibold leading-snug mt-1">{ev.title}</div>
                {ev.description && <div className="text-lg text-slate-200 leading-relaxed mt-1.5">{ev.description}</div>}
                {ev.source && <div className="text-base text-slate-400 mt-2">Πηγή: {ev.source}</div>}
              </div>
            ))}
          </div>
        )}

        {tab === "cross" && (
          <div className="space-y-3">
            {crossOutputs.length === 0 && (
              <div className="text-slate-400 text-lg py-8 text-center">
                Δεν υπάρχουν αναλύσεις αντεξέτασης. Δημιουργήστε από τις Εντολές AI.
              </div>
            )}
            {crossOutputs.map(o => (
              <div key={o.id} className="rounded-xl bg-white/5 border border-white/15 overflow-hidden">
                <button onClick={() => setOpenCross(openCross === o.id ? null : o.id)}
                  className="w-full flex items-center justify-between gap-2 p-4 text-left">
                  <div>
                    <div className="text-xl font-semibold">{o.title}</div>
                    <div className="text-base text-slate-400 mt-0.5">
                      {new Date(o.created_at).toLocaleDateString("el-GR")}
                    </div>
                  </div>
                  {openCross === o.id ? <ChevronUp className="size-6 text-amber-300 shrink-0"/> : <ChevronDown className="size-6 text-amber-300 shrink-0"/>}
                </button>
                {openCross === o.id && (
                  <div className="px-4 pb-4 space-y-2">
                    {mdToPlainBlocks(o.output).map((line, i) => {
                      const isHead = line.startsWith("#");
                      const isBullet = /^[-*•]|^\d+\./.test(line);
                      return (
                        <div key={i} className={
                          isHead ? "text-amber-300 text-xl font-bold pt-3"
                          : isBullet ? "text-lg leading-relaxed pl-3 border-l-2 border-amber-500/40"
                          : "text-lg leading-relaxed text-slate-200"
                        }>
                          {line.replace(/^#+\s*/, "").replace(/\*\*/g, "")}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "evidence" && (
          <div className="space-y-3">
            {evidence.length === 0 && <div className="text-slate-400 text-lg py-8 text-center">Δεν υπάρχουν αποδεικτικά</div>}
            {evidence.map(e => (
              <div key={e.id} className="rounded-xl bg-white/5 border border-white/15 p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {e.evidence_id && <span className="font-mono text-base px-2 py-0.5 rounded bg-amber-500/15 text-amber-300">{e.evidence_id}</span>}
                  <span className="text-xl font-semibold">{e.title}</span>
                </div>
                {e.description && <div className="text-lg text-slate-200 leading-relaxed mt-1.5">{e.description}</div>}
                {e.status && <div className="text-base text-slate-400 mt-2">Κατάσταση: {e.status}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
